var Previewer = new (class Previewer extends AbstractModule {
  constructor () {
    super("Previewer");

    this._URL = '/api/oembed?url=';
    this._pendingOembed = {};
    this._pendingClientPreview = {};

    // --- Config preview client ---
    this._clientPreviewEnabled = true;
    this._CLIENT_TIMEOUT_MS    = 5000;
    // Domaines intranet de confiance pour lesquels on autorise un fetch credentialed.
    // Le same-origin est toujours autorisé (cf. _shouldTryClient).
    this._clientPreviewDomains = [];
  }

  buildPreview (displayable) {
    const preview = new Preview(displayable, this.app);
    return preview;
  }

  // Point d'entrée : cascade client -> serveur
  getPreviewData (url, language = this.app._language) {
    if (this._shouldTryClient(url)) {
      return this.clientPreviewRequest(url, language)
        .catch((err) => {
          Utils.warn && Utils.warn('[Previewer] client preview KO, fallback serveur:', err && err.message);
          return this.oembedRequest(url, language);
        });
    }
    return this.oembedRequest(url, language);
  }

  // Tier 2 (inchangé)
  oembedRequest (url, language = this.app._language) {
    const key = encodeURIComponent(url) + '-' + language;

    if (!this._pendingOembed[key]) {
      this._pendingOembed[key] = Ajax.call(this._URL + encodeURIComponent(url) + '&lang=' + language)
        .then(result => {
          delete this._pendingOembed[key];
          return result;
        })
        .catch(err => {
          delete this._pendingOembed[key];
          throw err;
        });
    }

    return this._pendingOembed[key];
  }

  // --- Tier 1 : previewer client ---

  clientPreviewRequest (url, language = this.app._language) {
    const key = encodeURIComponent(url) + '-' + language;

    if (!this._pendingClientPreview[key]) {
      this._pendingClientPreview[key] = this._clientPreviewRequest(url, language)
        .then(result => {
          delete this._pendingClientPreview[key];
          return result;
        })
        .catch(err => {
          delete this._pendingClientPreview[key];
          throw err;
        });
    }

    return this._pendingClientPreview[key];
  }

  _shouldTryClient (url) {
    if (!this._clientPreviewEnabled) return false;
    if (typeof fetch === 'undefined' ||
        typeof AbortController === 'undefined' ||
        typeof DOMParser === 'undefined') return false;

    const pageHref     = this._pageHref();
    const pageHostname = this._pageHostname();

    let host;
    try { host = new URL(url, pageHref).hostname; }
    catch (e) { return false; }

    if (host === pageHostname) return true;

    return this._clientPreviewDomains.some(d => host === d || host.endsWith('.' + d));
  }

  _pageHostname () {
    const parser = this.app && this.app._askTechParser;
    if (parser && parser._pageHostname) return parser._pageHostname;
    try { return Dom.parent.location.hostname || ''; }
    catch (e) { return ''; }
  }

  _pageHref () {
    const parser = this.app && this.app._askTechParser;
    if (parser && parser._pageHref) return parser._pageHref;
    try { return Dom.parent.location.href; }
    catch (e) { return typeof location !== 'undefined' ? location.href : ''; }
  }

  _clientPreviewRequest (url, language) {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this._CLIENT_TIMEOUT_MS);

      fetch(url, {
        method:      'GET',
        credentials: 'include',   // envoie les cookies de session (auth intranet)
        redirect:    'follow',
        signal:      controller.signal,
        headers:     { 'Accept': 'text/html,application/xhtml+xml' }
      })
      .then(response => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const ct = response.headers.get('content-type') || '';
        if (!/text\/html|application\/xhtml/i.test(ct)) throw new Error('content-type non-HTML: ' + ct);
        return response.text();
      })
      .then(html => {
        const linkData = this._parseHtmlMeta(html, url);
        if (!linkData) throw new Error('aucune metadata exploitable');
        resolve(linkData);
      })
      .catch(reject)
      .then(() => clearTimeout(timer), () => clearTimeout(timer));
    });
  }

  _parseHtmlMeta (html, url) {
    let doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); }
    catch (e) { return null; }

    const pick = (selectors) => {
      for (let i = 0; i < selectors.length; i++) {
        const el = doc.querySelector(selectors[i]);
        if (el) {
          const val = el.getAttribute('content') || el.getAttribute('href');
          if (val && val.trim()) return val.trim();
        }
      }
      return null;
    };

    const absolute = (val, base) => {
      if (!val) return null;
      try { return new URL(val, base).href; } catch (e) { return val; }
    };

    const canonical = pick(['meta[property="og:url"]', 'link[rel="canonical"]']) || url;

    const title = pick([
      'meta[property="og:title"]',
      'meta[name="twitter:title"]',
      'meta[name="title"]'
    ]) || (doc.querySelector('title') ? doc.querySelector('title').textContent.trim() : null);

    let description = pick([
      'meta[property="og:description"]',
      'meta[name="twitter:description"]',
      'meta[name="description"]'
    ]);

    const image = pick([
      'meta[property="og:image:secure_url"]',
      'meta[property="og:image:url"]',
      'meta[property="og:image"]',
      'meta[name="twitter:image"]',
      'meta[name="twitter:image:src"]'
    ]);

    const favicon = pick([
      'link[rel="icon"]',
      'link[rel="shortcut icon"]',
      'link[rel="apple-touch-icon"]',
      'link[rel="apple-touch-icon-precomposed"]'
    ]);

    const siteName = pick(['meta[property="og:site_name"]']);

    // Rien d'exploitable -> on laisse le tier suivant tenter sa chance
    if (!title && !description && !image) return null;

    let host = '', provider_url = canonical;
    try {
      const u = new URL(canonical);
      host = u.hostname;
      provider_url = u.origin;
    } catch (e) {}

    if (description && description.length > 120) {
      description = description.slice(0, 121) + '...';
    }

    return {
      type:          'link',
      title:         title || host || url,
      description:   description || '',
      thumbnail_url: absolute(image, canonical),
      favicon:       absolute(favicon, canonical),
      provider_name: siteName || host,
      provider_url:  provider_url,
      url:           canonical,
      _source:       'client'
    };
  }

  isHyperLink (aTag) {
    return aTag.innerText != aTag.getAttribute('href');
  }
});